import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { supabase } from '../../../lib/supabaseClient';
import Spinner from '../../Atoms/Spinner';
import toast from 'react-hot-toast';

const EVENT_ID = '67590da5499bcc995c210b40';

// ─── Styled Components ──────────────────────────────────
const Container = styled.div`
    max-width: 1800px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Header = styled.div`
    margin-bottom: 40px;
    h1 { font-size: 32px; margin-bottom: 8px; color: #333; }
    .subtitle { font-size: 18px; color: #666; }
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;
    margin-bottom: 20px;
    &:hover { background: #e8e8e8; transform: translateX(-4px); }
`;

const StatsSection = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
`;

const StatCard = styled.div`
    background: ${props => props.color || 'white'};
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    .value { font-size: 36px; font-weight: 700; color: ${props => props.textColor || '#333'}; }
    .label { font-size: 14px; color: ${props => props.textColor ? `${props.textColor}aa` : '#666'}; margin-top: 8px; }
`;

const TableSection = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
    padding: 30px;
    overflow-x: auto;
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
    h2 { font-size: 20px; color: #333; margin: 0; }
`;

const HeaderActions = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
`;

const SearchInput = styled.input`
    padding: 10px 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    width: 280px;
    transition: border-color 0.2s;
    &:focus { outline: none; border-color: #e67e22; }
`;

const ActionButton = styled.button`
    padding: 10px 20px;
    background: ${props => {
        if (props.variant === 'primary') return 'linear-gradient(135deg, #e67e22, #d35400)';
        if (props.variant === 'success') return 'linear-gradient(135deg, #27ae60, #229954)';
        if (props.variant === 'danger') return 'linear-gradient(135deg, #e74c3c, #c0392b)';
        return 'linear-gradient(135deg, #ecf0f1, #bdc3c7)';
    }};
    color: ${props => props.variant ? 'white' : '#333'};
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    th {
        text-align: left;
        padding: 14px 12px;
        background: #f8f9fa;
        font-weight: 600;
        font-size: 13px;
        color: #555;
        border-bottom: 2px solid #e0e0e0;
        white-space: nowrap;
    }
    td {
        padding: 12px;
        border-bottom: 1px solid #f0f0f0;
        font-size: 14px;
        vertical-align: middle;
    }
    tr:hover td { background: #fafafa; }
`;

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    background: ${props => {
        if (props.type === 'oui') return '#d4edda';
        if (props.type === 'non') return '#f8d7da';
        if (props.type === 'pending') return '#fff3cd';
        if (props.type === 'contact') return '#d1ecf1';
        if (props.type === 'objet') return '#e8f4f8';
        if (props.type === 'espace') return '#fef3e2';
        return '#e9ecef';
    }};
    color: ${props => {
        if (props.type === 'oui') return '#155724';
        if (props.type === 'non') return '#721c24';
        if (props.type === 'pending') return '#856404';
        if (props.type === 'contact') return '#0c5460';
        if (props.type === 'objet') return '#1a5276';
        if (props.type === 'espace') return '#e67e22';
        return '#495057';
    }};
`;

const PhotoThumb = styled.img`
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 6px;
    cursor: pointer;
    transition: transform 0.2s;
    &:hover { transform: scale(1.1); }
`;

const NoPhoto = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 6px;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ccc;
    font-size: 20px;
`;

const CommentInput = styled.input`
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 13px;
    width: 100%;
    min-width: 150px;
    &:focus { outline: none; border-color: #e67e22; }
`;

const PieceRow = styled.tr`
    td {
        background: ${props => {
            if (props.submitted) return '#e8f5e9 !important';
            return 'inherit';
        }};
    }
    &:hover td {
        background: ${props => {
            if (props.submitted) return '#c8e6c9 !important';
            return '#fafafa';
        }};
    }
`;

const LightboxOverlay = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.85);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    img { max-width: 90vw; max-height: 90vh; object-fit: contain; border-radius: 8px; }
`;

const UploadLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    font-size: 12px;
    color: #3498db;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background 0.2s;
    &:hover { background: #eaf2ff; }
    input { display: none; }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: #999;
    font-size: 16px;
`;

// ─── Main Component ─────────────────────────────────────
const FD100Exposition = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [expoFinalePieces, setExpoFinalePieces] = useState([]);

    const [exporting, setExporting] = useState(false);

    // Inline editing
    const [efEditing, setEfEditing] = useState(null); // { id, field }
    const [efDraft, setEfDraft] = useState('');

    // ─── Fetch data from fd100_expo_finale_pieces ─────────
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('fd100_expo_finale_pieces')
                .select('*')
                .eq('event_id', EVENT_ID)
                .order('nom_piece');
            if (error) throw error;
            const HIDDEN_PIECES = ['totémique', 'totemique', 'ishigaki'];
            const visible = (data || []).filter(p => !HIDDEN_PIECES.includes((p.nom_piece || '').toLowerCase().trim()));
            setExpoFinalePieces(visible);
        } catch (err) {
            console.error('Error fetching expo finale data:', err);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ─── Stats ────────────────────────────────────────────
    const stats = useMemo(() => {
        const total = expoFinalePieces.length;
        const submitted = expoFinalePieces.filter(p => p.submitted_at).length;
        const withContrat = expoFinalePieces.filter(p => p.contrat_admin_url).length;
        const contratSigne = expoFinalePieces.filter(p => p.contrat_signe_url).length;
        return { total, submitted, withContrat, contratSigne };
    }, [expoFinalePieces]);

    // ─── Inline editing save ──────────────────────────────
    const saveField = async (pieceId, field) => {
        const value = efDraft.trim();
        const { error } = await supabase
            .from('fd100_expo_finale_pieces')
            .update({ [field]: value, updated_at: new Date().toISOString() })
            .eq('id', pieceId);
        if (error) { toast.error('Erreur sauvegarde'); return; }
        setExpoFinalePieces(prev => prev.map(p =>
            p.id === pieceId ? { ...p, [field]: value } : p
        ));
        setEfEditing(null);
        toast.success('Sauvegardé');
    };

    const efCell = (pieceId, field, currentValue) => {
        if (efEditing?.id === pieceId && efEditing?.field === field) {
            return (
                <div style={{ display: 'flex', gap: 4 }}>
                    <CommentInput
                        value={efDraft}
                        onChange={e => setEfDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveField(pieceId, field); if (e.key === 'Escape') setEfEditing(null); }}
                        autoFocus
                        style={{ minWidth: 100 }}
                    />
                    <ActionButton variant="success" onClick={() => saveField(pieceId, field)} style={{ padding: '4px 8px', fontSize: 11 }}>OK</ActionButton>
                </div>
            );
        }
        return (
            <div
                style={{ cursor: 'pointer', color: currentValue ? '#333' : '#ccc', minHeight: 22 }}
                onClick={() => { setEfEditing({ id: pieceId, field }); setEfDraft(currentValue || ''); }}
                title="Cliquer pour éditer"
            >
                {currentValue || 'Ajouter...'}
            </div>
        );
    };

    // ─── Contract upload ──────────────────────────────────
    const handleContratUpload = async (pieceId, file) => {
        const acceptedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!file || !acceptedTypes.includes(file.type)) {
            toast.error('Fichier PDF ou DOCX uniquement');
            return;
        }
        const ext = file.name.split('.').pop().toLowerCase() || 'pdf';
        const fileName = `fd100-contrats/${pieceId}_${Date.now()}.${ext}`;
        const contentType = file.type === 'application/pdf' ? 'application/pdf' : 'application/octet-stream';
        const { error: uploadError } = await supabase.storage
            .from('fd100-documents')
            .upload(fileName, file, { contentType, upsert: true });
        if (uploadError) {
            toast.error('Erreur upload: ' + uploadError.message);
            return;
        }
        const { data: { publicUrl } } = supabase.storage
            .from('fd100-documents')
            .getPublicUrl(fileName);

        const { error: updateError } = await supabase
            .from('fd100_expo_finale_pieces')
            .update({ contrat_admin_url: publicUrl, updated_at: new Date().toISOString() })
            .eq('id', pieceId);
        if (updateError) {
            toast.error('Erreur sauvegarde');
            return;
        }
        setExpoFinalePieces(prev => prev.map(p =>
            p.id === pieceId ? { ...p, contrat_admin_url: publicUrl } : p
        ));
        toast.success('Contrat uploadé');
    };

    // ─── Export ZIP ────────────────────────────────────────
    const sanitize = (str) => (str || 'sans-nom').replace(/[/\\?%*:|"<>]/g, '-').trim();

    const handleExportZip = async () => {
        if (exporting) return;
        setExporting(true);
        const toastId = toast.loading('Préparation du ZIP...');
        try {
            const zip = new JSZip();
            const csvRows = [['N°', 'Pièce', 'Designer', 'Fabricant', 'Éditeur', 'Catégorie photo', 'Crédit photo', 'Nom du fichier', 'URL originale']];
            let photoIndex = 0;
            let downloaded = 0;
            const totalPhotos = expoFinalePieces.reduce((n, p) => n + (p.photos || []).length, 0);

            for (const piece of expoFinalePieces) {
                const photos = piece.photos || [];
                if (photos.length === 0) continue;

                const folderName = sanitize(piece.nom_piece);
                const folder = zip.folder(folderName);

                for (let i = 0; i < photos.length; i++) {
                    const photo = photos[i];
                    if (!photo.url) continue;

                    photoIndex++;
                    const category = photo.category || 'non-classée';
                    const credit = photo.credit || '';
                    const ext = (photo.url.split('.').pop() || 'jpg').split('?')[0].toLowerCase();
                    const fileName = `${folderName}_${category}_${i + 1}.${ext}`;

                    try {
                        const resp = await fetch(photo.url);
                        if (!resp.ok) throw new Error(resp.status);
                        const blob = await resp.blob();
                        folder.file(fileName, blob);
                        downloaded++;
                        toast.loading(`Téléchargement ${downloaded}/${totalPhotos}...`, { id: toastId });
                    } catch (err) {
                        console.warn(`Échec téléchargement: ${photo.url}`, err);
                        folder.file(`${fileName}.ERREUR.txt`, `Impossible de télécharger: ${photo.url}\n${err.message}`);
                    }

                    const csvVal = (v) => `"${(v || '').replace(/"/g, '""')}"`;
                    csvRows.push([
                        photoIndex,
                        csvVal(piece.nom_piece),
                        csvVal(piece.designer_names),
                        csvVal(piece.fabricant),
                        csvVal(piece.editeur),
                        csvVal(category === 'packshot' ? 'Packshot' : category === 'context' ? 'Contexte' : category),
                        csvVal(credit),
                        csvVal(fileName),
                        csvVal(photo.url),
                    ].join(','));
                }
            }

            // Add CSV index
            const bom = '\uFEFF';
            zip.file('_INDEX_MEDIAS.csv', bom + csvRows.join('\n'));

            toast.loading('Génération du ZIP...', { id: toastId });
            const blob = await zip.generateAsync({ type: 'blob' }, (meta) => {
                toast.loading(`Compression ${Math.round(meta.percent)}%...`, { id: toastId });
            });

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `FD100_Expo_Medias_${new Date().toISOString().slice(0, 10)}.zip`;
            a.click();
            URL.revokeObjectURL(url);

            toast.success(`${downloaded} photos exportées`, { id: toastId });
        } catch (err) {
            console.error('Export error:', err);
            toast.error('Erreur lors de l\'export', { id: toastId });
        } finally {
            setExporting(false);
        }
    };

    // ─── Export CRM XLSX ─────────────────────────────────
    const handleExportCRM = () => {
        const contactMap = new Map();
        for (const piece of expoFinalePieces) {
            const email = (piece.contact_email || '').trim().toLowerCase();
            if (!email) continue;
            if (!contactMap.has(email)) {
                contactMap.set(email, {
                    prenom: piece.contact_prenom || '',
                    nom: piece.contact_nom || '',
                    entreprise: piece.fabricant || piece.editeur || '',
                    email: piece.contact_email || '',
                    telephone: piece.contact_telephone || '',
                    codifab: piece.codifab_af || '',
                });
            }
        }

        const headers = [
            'PRENOM', 'NOM', 'CIVILITÉ', "NOM DE L'ENTREPRISE", 'EMAIL',
            'EMAIL SECONDAIRE', 'FONCTION', 'WEBSITE', 'TELEPHONE',
            'ADRESSE', 'CODE POSTAL', 'VILLE', 'PAYS',
            'CATEGORIE', 'CATEGORIE (2)', 'PROGRAMMES', 'CODIFAB', 'SOURCE',
        ];

        const rows = [headers];
        for (const c of contactMap.values()) {
            rows.push([
                c.prenom,
                c.nom,
                '',              // CIVILITÉ
                c.entreprise,
                c.email,
                '',              // EMAIL SECONDAIRE
                '',              // FONCTION
                '',              // WEBSITE
                c.telephone,
                '',              // ADRESSE
                '',              // CODE POSTAL
                '',              // VILLE
                'France',        // PAYS
                '',              // CATEGORIE
                '',              // CATEGORIE (2)
                'FD100 Expo Finale 2026', // PROGRAMMES
                c.codifab,
                'FD100 Exposition',       // SOURCE
            ]);
        }

        const ws = XLSX.utils.aoa_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ListeVierge');
        XLSX.writeFile(wb, `FD100_Expo_Contacts_CRM_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success(`${contactMap.size} contacts exportés`);
    };

    // ─── Export Complet XLSX ─────────────────────────────
    const handleExportComplet = () => {
        const BASE_URL = 'https://connectedby.lefrenchdesign.org';

        // ── Sheet 1: Pièces (one row per piece) ──
        const piecesHeaders = [
            'N°', 'Nom de la pièce', 'Type', 'Designer', 'Fabricant', 'Éditeur',
            'CODIFAB/AF', "Date d'édition", 'Nom du projet', 'Partenaire',
            'Typologie', 'Dimensions', 'Matériaux', 'Poids', 'Valeur assurance',
            'Description',
            'Contact prénom', 'Contact nom', 'Contact email', 'Contact téléphone',
            'Formulaire soumis', 'Date soumission',
            'Contrat envoyé', 'Contrat signé', 'Date contrat signé',
            'Nb photos', 'Photos (URLs)', 'Photos (crédits)', 'Photos (catégories)',
            'Lien fiche', 'Token',
        ];
        const piecesRows = [piecesHeaders];

        expoFinalePieces.forEach((piece, idx) => {
            const photos = piece.photos || [];
            const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '';
            piecesRows.push([
                idx + 1,
                piece.nom_piece || '',
                piece.type || '',
                piece.designer_names || '',
                piece.fabricant || '',
                piece.editeur || '',
                piece.codifab_af || '',
                piece.date_edition || '',
                piece.project_name || '',
                piece.partenaire_name || '',
                piece.typologie || '',
                piece.dimensions || '',
                piece.materiaux || '',
                piece.poids || '',
                piece.valeur_assurance || '',
                piece.description || '',
                piece.contact_prenom || '',
                piece.contact_nom || '',
                piece.contact_email || '',
                piece.contact_telephone || '',
                piece.submitted_at ? 'Oui' : 'Non',
                fmtDate(piece.submitted_at),
                piece.contrat_admin_url ? 'Oui' : 'Non',
                piece.contrat_signe_url ? 'Oui' : 'Non',
                fmtDate(piece.contrat_signe_at),
                photos.length,
                photos.map(p => p.url || '').join('\n'),
                photos.map(p => p.credit || '').join('\n'),
                photos.map(p => p.category || '').join('\n'),
                `${BASE_URL}/expo/finale?piece=${piece.id}`,
                piece.token || '',
            ]);
        });

        // ── Sheet 2: Médias (one row per photo) ──
        const mediasHeaders = ['Pièce', 'Designer', 'Fabricant', 'Catégorie', 'Crédit', 'URL'];
        const mediasRows = [mediasHeaders];

        for (const piece of expoFinalePieces) {
            for (const photo of (piece.photos || [])) {
                mediasRows.push([
                    piece.nom_piece || '',
                    piece.designer_names || '',
                    piece.fabricant || '',
                    photo.category || '',
                    photo.credit || '',
                    photo.url || '',
                ]);
            }
        }

        const wb = XLSX.utils.book_new();

        const wsPieces = XLSX.utils.aoa_to_sheet(piecesRows);
        wsPieces['!cols'] = piecesHeaders.map((h) => ({
            wch: Math.max(h.length, 14),
        }));
        XLSX.utils.book_append_sheet(wb, wsPieces, 'Pièces');

        const wsMedias = XLSX.utils.aoa_to_sheet(mediasRows);
        wsMedias['!cols'] = mediasHeaders.map((h) => ({
            wch: Math.max(h.length, 14),
        }));
        XLSX.utils.book_append_sheet(wb, wsMedias, 'Médias');

        XLSX.writeFile(wb, `FD100_Expo_Complet_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success(`${expoFinalePieces.length} pièces exportées (${mediasRows.length - 1} médias)`);
    };

    // ─── Filtered pieces ──────────────────────────────────
    const filteredPieces = useMemo(() => {
        if (!searchTerm.trim()) return expoFinalePieces;
        const term = searchTerm.toLowerCase();
        return expoFinalePieces.filter(p =>
            (p.nom_piece || '').toLowerCase().includes(term) ||
            (p.designer_names || '').toLowerCase().includes(term) ||
            (p.fabricant || '').toLowerCase().includes(term) ||
            (p.codifab_af || '').toLowerCase().includes(term) ||
            (p.editeur || '').toLowerCase().includes(term) ||
            (p.date_edition || '').toLowerCase().includes(term) ||
            (p.contact_email || '').toLowerCase().includes(term)
        );
    }, [expoFinalePieces, searchTerm]);

    // ─── Render ───────────────────────────────────────────
    if (loading) {
        return (
            <Container>
                <div style={{ textAlign: 'center', padding: '80px 0' }}>
                    <Spinner />
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <BackButton onClick={() => navigate('/admin')}>← Retour</BackButton>

            <Header>
                <h1>Exposition FD100</h1>
                <div className="subtitle">Expo Finale — Pièces sélectionnées par le scénographe</div>
            </Header>

            <StatsSection>
                <StatCard color="#e8f4f8" textColor="#1a5276">
                    <div className="value">{stats.total}</div>
                    <div className="label">Pièces total</div>
                </StatCard>
                <StatCard color="#d4edda" textColor="#155724">
                    <div className="value">{stats.submitted}</div>
                    <div className="label">Formulaires soumis</div>
                </StatCard>
                <StatCard color="#fef3e2" textColor="#d35400">
                    <div className="value">{stats.withContrat}</div>
                    <div className="label">Contrats uploadés</div>
                </StatCard>
                <StatCard color="#f5eef8" textColor="#8e44ad">
                    <div className="value">{stats.contratSigne}</div>
                    <div className="label">Contrats signés</div>
                </StatCard>
            </StatsSection>

            {lightboxUrl && (
                <LightboxOverlay onClick={() => setLightboxUrl(null)}>
                    <img src={lightboxUrl} alt="" />
                </LightboxOverlay>
            )}

            <TableSection>
                <TableHeader>
                    <h2>Expo Finale ({filteredPieces.length} pièces)</h2>
                    <HeaderActions>
                        <SearchInput placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        <ActionButton onClick={fetchData}>Actualiser</ActionButton>
                        <ActionButton variant="primary" onClick={handleExportZip} disabled={exporting}>
                            {exporting ? 'Export en cours...' : 'Export ZIP médias'}
                        </ActionButton>
                        <ActionButton variant="success" onClick={handleExportCRM}>
                            Export CRM
                        </ActionButton>
                        <ActionButton variant="primary" onClick={handleExportComplet}>
                            Export complet
                        </ActionButton>
                    </HeaderActions>
                </TableHeader>

                {filteredPieces.length === 0 ? (
                    <EmptyState>Aucune pièce trouvée</EmptyState>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th>N°</th>
                                <th>Photo</th>
                                <th>Nom de la pièce</th>
                                <th>Designer</th>
                                <th>Fabricant</th>
                                <th>CODIFAB/AF</th>
                                <th>Éditeur</th>
                                <th>Date d'édition</th>
                                <th>Typologie</th>
                                <th>Dimensions</th>
                                <th>Matériaux</th>
                                <th>Poids</th>
                                <th>Val. assurance</th>
                                <th>Contact</th>
                                <th>Email</th>
                                <th>Tél</th>
                                <th style={{ textAlign: 'center' }}>Soumis</th>
                                <th>Contrat</th>
                                <th style={{ textAlign: 'center' }}>Contrat signé</th>
                                <th>Infos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPieces.map((piece, idx) => {
                                const firstPhoto = (piece.photos || [])[0];
                                return (
                                    <PieceRow key={piece.id} submitted={!!piece.submitted_at}>
                                        <td>{idx + 1}</td>
                                        <td>
                                            {firstPhoto?.url ? (
                                                <PhotoThumb src={firstPhoto.url} alt="" onClick={() => setLightboxUrl(firstPhoto.url)} />
                                            ) : (
                                                <NoPhoto>—</NoPhoto>
                                            )}
                                        </td>
                                        <td><strong>{efCell(piece.id, 'nom_piece', piece.nom_piece)}</strong></td>
                                        <td>{efCell(piece.id, 'designer_names', piece.designer_names)}</td>
                                        <td>{efCell(piece.id, 'fabricant', piece.fabricant)}</td>
                                        <td>{efCell(piece.id, 'codifab_af', piece.codifab_af)}</td>
                                        <td>{efCell(piece.id, 'editeur', piece.editeur)}</td>
                                        <td>{efCell(piece.id, 'date_edition', piece.date_edition)}</td>
                                        <td>{efCell(piece.id, 'typologie', piece.typologie)}</td>
                                        <td>{efCell(piece.id, 'dimensions', piece.dimensions)}</td>
                                        <td>{efCell(piece.id, 'materiaux', piece.materiaux)}</td>
                                        <td>{efCell(piece.id, 'poids', piece.poids)}</td>
                                        <td>{efCell(piece.id, 'valeur_assurance', piece.valeur_assurance)}</td>
                                        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                                            {piece.contact_prenom || ''} {piece.contact_nom || ''}
                                        </td>
                                        <td style={{ fontSize: 12 }}>{piece.contact_email || '—'}</td>
                                        <td style={{ fontSize: 12 }}>{piece.contact_telephone || '—'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {piece.submitted_at ? (
                                                <Badge type="oui">Soumis</Badge>
                                            ) : (
                                                <Badge type="pending">En attente</Badge>
                                            )}
                                        </td>
                                        <td>
                                            {piece.contrat_admin_url ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    <a href={piece.contrat_admin_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#27ae60', fontWeight: 600 }}>
                                                        Voir PDF
                                                    </a>
                                                    <UploadLabel>
                                                        Remplacer
                                                        <input type="file" accept="application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                            onChange={e => e.target.files[0] && handleContratUpload(piece.id, e.target.files[0])} />
                                                    </UploadLabel>
                                                </div>
                                            ) : (
                                                <UploadLabel style={{ color: '#e67e22', fontWeight: 600 }}>
                                                    Uploader PDF
                                                    <input type="file" accept="application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                        onChange={e => e.target.files[0] && handleContratUpload(piece.id, e.target.files[0])} />
                                                </UploadLabel>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {piece.contrat_signe_url ? (
                                                <a href={piece.contrat_signe_url} target="_blank" rel="noreferrer" title="Télécharger le contrat signé">
                                                    <Badge type="oui">Signé</Badge>
                                                </a>
                                            ) : (
                                                <Badge type="pending">En attente</Badge>
                                            )}
                                        </td>
                                        <td>
                                            <a
                                                href={`/expo/finale?piece=${piece.id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ fontSize: 12, color: '#3498db', whiteSpace: 'nowrap' }}
                                            >
                                                Voir fiche
                                            </a>
                                        </td>
                                    </PieceRow>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </TableSection>
        </Container>
    );
};

export default FD100Exposition;
